import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { FiSend, FiGithub, FiLinkedin, FiMail } from 'react-icons/fi';

const schema = Yup.object({
  fullName: Yup.string().required('Required'),
  email: Yup.string().email('Invalid email').required('Required'),
  message: Yup.string().min(20, 'Too short').required('Required'),
});

const socials = [
  { Icon: FiGithub, href: 'https://github.com/arbhram', label: 'GitHub', handle: 'github.com/arbhram' },
  { Icon: FiLinkedin, href: 'https://www.linkedin.com/in/aryan-dev-pandey-03ab62255/', label: 'LinkedIn', handle: 'linkedin.com/in/aryan-dev-pandey' },
  { Icon: FiMail, href: 'mailto:aryandevpandey22@gmail.com', label: 'Email', handle: 'aryandevpandey22@gmail.com' },
];

export default function Contact() {
  const [status, setStatus] = useState(null);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const handleSubmit = (_values, { resetForm, setSubmitting }) => {
    setStatus('sending');
    setTimeout(() => {
      setStatus('sent');
      resetForm();
      setSubmitting(false);
      setTimeout(() => setStatus(null), 5000);
    }, 1500);
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px',
    padding: '0.9rem 1.2rem',
    color: '#f0f6ff',
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.9rem',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };

  return (
    <section id="contact" style={{ padding: '120px 0' }}>
      <div className="section-container">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '5rem', alignItems: 'start' }}>

          <motion.div ref={ref} initial={{ opacity: 0, x: -32 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}>
            <p className="section-label">Contact</p>
            <h2 className="section-title" style={{ marginBottom: '1.25rem' }}>
              Let's build something{' '}
              <span style={{ background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                great
              </span>
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.85, marginBottom: '2.5rem', maxWidth: 360 }}>
              Looking for a junior or graduate developer role? I'm actively seeking opportunities.
              Whether it's a project, internship, or full-time position — I'd love to chat.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {socials.map(({ Icon, href, label, handle }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem', textDecoration: 'none', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.querySelector('.social-box').style.borderColor = 'rgba(99,102,241,0.4)'}
                  onMouseLeave={e => e.currentTarget.querySelector('.social-box').style.borderColor = 'rgba(255,255,255,0.08)'}
                >
                  <span className="social-box" style={{ width: 40, height: 40, background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '1rem', transition: 'border-color 0.2s', flexShrink: 0 }}>
                    <Icon />
                  </span>
                  <div>
                    <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.82rem', fontWeight: 600, color: '#f0f6ff', marginBottom: '0.1rem' }}>{label}</p>
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.72rem', color: '#475569' }}>{handle}</p>
                  </div>
                </a>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 32 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}>
            {status === 'sent' && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem', fontFamily: 'Inter, sans-serif', color: '#4ade80', fontSize: '0.88rem' }}>
                ✓ Message sent! I'll get back to you soon.
              </motion.div>
            )}

            <Formik initialValues={{ fullName: '', email: '', message: '' }} validationSchema={schema} onSubmit={handleSubmit}>
              {({ errors, touched, isSubmitting }) => (
                <Form style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                  {[{ name: 'fullName', placeholder: 'Your full name', type: 'text' }, { name: 'email', placeholder: 'your@email.com', type: 'email' }].map(({ name, placeholder, type }) => (
                    <div key={name}>
                      <Field name={name} type={type} placeholder={placeholder}
                        style={{ ...inputStyle, borderColor: errors[name] && touched[name] ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.08)' }}
                        onFocus={e => { e.target.style.borderColor = 'rgba(99,102,241,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.07)'; }}
                        onBlur={e => { e.target.style.borderColor = errors[name] ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
                      />
                      {errors[name] && touched[name] && <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', color: '#f87171', marginTop: '0.35rem' }}>{errors[name]}</p>}
                    </div>
                  ))}
                  <div>
                    <Field as="textarea" name="message" placeholder="Tell me about the opportunity or project..." rows={5}
                      style={{ ...inputStyle, resize: 'vertical', minHeight: 130, borderColor: errors.message && touched.message ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.08)' }}
                      onFocus={e => { e.target.style.borderColor = 'rgba(99,102,241,0.5)'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.07)'; }}
                      onBlur={e => { e.target.style.borderColor = errors.message ? 'rgba(248,113,113,0.4)' : 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
                    />
                    {errors.message && touched.message && <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', color: '#f87171', marginTop: '0.35rem' }}>{errors.message}</p>}
                  </div>

                  <motion.button type="submit" disabled={isSubmitting || status === 'sending'} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.55rem', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '12px', padding: '1rem', fontFamily: 'Space Grotesk, sans-serif', fontSize: '0.9rem', fontWeight: 700, opacity: isSubmitting ? 0.7 : 1, boxShadow: '0 8px 32px rgba(99,102,241,0.3)', transition: 'opacity 0.2s' }}>
                    <FiSend size={15} />
                    {status === 'sending' ? 'Sending...' : 'Send Message'}
                  </motion.button>
                </Form>
              )}
            </Formik>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
